import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  Buildings, Bell, MagnifyingGlass, SignOut, List, X, Gauge, Briefcase, Folders,
  ChartBar, ShieldCheck, Users, GitBranch, ClipboardText, Stack, Sliders, EnvelopeSimple,
  ArrowsLeftRight, FileText, TreeStructure, Lock, CalendarCheck, Table, SidebarSimple, CaretDown,
  Presentation, ArrowsOutSimple, LinkSimple, Vault, Minus as MinusIcon, Plus as PlusIcon,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { usePresentation } from "@/context/PresentationContext";
import { useAutoFit } from "@/lib/useAutoFit";
import { api, FRAMEWORK_META, LOGO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NavItem = ({ to, icon: Icon, label, testid, accent }) => (
  <NavLink
    to={to}
    end
    data-testid={testid}
    className={({ isActive }) =>
      `group relative flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150 ${
        isActive
          ? "bg-navy/[0.07] font-semibold text-navy"
          : "text-muted-foreground hover:bg-navy/[0.04] hover:text-navy"
      }`
    }
  >
    {({ isActive }) => (
      <>
        <span
          className={`absolute inset-y-1 left-0 w-[3px] rounded-r-full transition-all duration-300 ${
            accent || "bg-deepblue"
          } ${isActive ? "opacity-100" : "opacity-0 group-hover:opacity-60"}`}
        />
        <Icon size={16} weight={isActive ? "duotone" : "light"} />
        <span className="truncate">{label}</span>
      </>
    )}
  </NavLink>
);

const Section = ({ title, children, testid }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mt-7">
      <button data-testid={testid} onClick={() => setOpen((v) => !v)}
              className="flex w-full items-center justify-between px-4 pb-2.5 text-left text-muted-foreground transition-colors hover:text-navy">
        <span className="label-caps">{title}</span>
        <CaretDown size={12} className={`transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="flex flex-col">{children}</div>}
    </div>
  );
};

export const AppShell = ({ children }) => {
  const { user, tenants, tenant, tenantId, switchTenant, logout, isPlatformAdmin, hasFramework, access } = useAuth();
  const { presenting, toggle } = usePresentation();
  const fit = useAutoFit();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/notifications/count").then(({ data }) => setUnread(data.unread)).catch(() => {});
  }, [tenantId]);

  const runSearch = async (e) => {
    e.preventDefault();
    if (!query.trim() || !tenantId) return;
    try {
      const { data } = await api.get(`/tenants/${tenantId}/search`, { params: { q: query } });
      setResults(data);
    } catch {
      setResults([]);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="aurora" />
      <div className="grain" />
      <div className="brand-bar h-1 w-full" />
      <header className="sticky top-0 z-30 border-b border-border/70 bg-background/80 backdrop-blur-xl">
        <div className="flex items-center gap-4 px-4 py-2.5 md:px-8">
          <button
            data-testid="mobile-nav-toggle"
            className="text-navy lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle navigation"
          >
            {open ? <X size={20} /> : <List size={20} />}
          </button>
          <Link to="/" className="flex items-center gap-3" data-testid="brand-home-link">
            <img src={LOGO} alt="Aikyam" className="h-9 w-auto" />
            <span className="label-caps hidden text-navy sm:inline">Family Business 360°</span>
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="tenant-switcher-button"
                variant="outline"
                className="ml-2 h-9 gap-2 border-deepblue/30 bg-card text-xs text-navy hover:bg-sky/20"
              >
                <Buildings size={14} className="text-deepblue" />
                <span className="max-w-[9rem] truncate">{tenant?.name || "Select engagement"}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72">
              <DropdownMenuLabel className="label-caps">Engagements</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {tenants.length === 0 && (
                <DropdownMenuItem disabled data-testid="tenant-empty">No engagements assigned</DropdownMenuItem>
              )}
              {tenants.map((t) => (
                <DropdownMenuItem
                  key={t.id}
                  data-testid={`tenant-option-${t.id}`}
                  onClick={() => {
                    switchTenant(t.id);
                    navigate("/");
                  }}
                  className="flex flex-col items-start gap-1"
                >
                  <span className="text-sm text-navy">{t.name}</span>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {(t.frameworks || []).map((f) => FRAMEWORK_META[f]?.name).join(" · ") || "No framework active"}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <form onSubmit={runSearch} className="ml-auto hidden items-center gap-2 md:flex">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-deepblue" />
              <Input
                data-testid="global-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search this engagement"
                className="h-9 w-56 border-border bg-card pl-9 text-xs"
              />
            </div>
          </form>

          <Link to="/notifications" data-testid="notifications-link" className="relative ml-auto text-navy md:ml-0">
            <Bell size={18} />
            {unread > 0 && (
              <span
                data-testid="notification-unread-count"
                className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-gold px-1 font-mono text-[10px] text-white"
              >
                {unread}
              </span>
            )}
          </Link>

          <button
            data-testid="presenter-view-toggle"
            onClick={fit.toggle}
            title={fit.on ? "Exit presenter view" : "Fit the whole screen — presenter view"}
            className={`hidden h-9 items-center gap-2 rounded-full border px-4 text-[10px] font-semibold uppercase tracking-[0.16em] transition-colors duration-300 sm:inline-flex ${
              fit.on
                ? "border-terracotta bg-terracotta text-white"
                : "border-terracotta/40 text-terracotta hover:bg-terracotta/10"
            }`}
          >
            <ArrowsOutSimple size={14} /> {fit.on ? "Presenter view on" : "Presenter view"}
          </button>

          <button
            data-testid="presentation-toggle"
            onClick={toggle}
            title={presenting ? "Exit client view" : "Client view"}
            className={`hidden h-9 items-center gap-2 rounded-full border px-4 text-[10px] font-semibold uppercase tracking-[0.16em] transition-colors duration-300 sm:inline-flex ${
              presenting
                ? "border-deepblue bg-deepblue text-white"
                : "border-border text-navy hover:border-deepblue hover:text-deepblue"
            }`}
          >
            <Presentation size={14} /> {presenting ? "Client view on" : "Client view"}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="profile-menu-button" variant="ghost" className="h-9 gap-2 px-2 text-xs text-navy hover:bg-sky/20">
                <span className="hidden max-w-[8rem] truncate sm:inline">{user?.name}</span>
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-deepblue to-navy font-mono text-[11px] text-white">
                  {(user?.name || "?").slice(0, 1)}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="flex flex-col gap-1">
                <span className="text-sm text-navy">{user?.name}</span>
                <span className="text-xs text-muted-foreground">{user?.email}</span>
                <span className="label-caps mt-1">{(access?.roles || []).join(", ") || "No role"}</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="profile-menu-preferences">
                Profile & preferences
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="logout-button" onClick={logout}>
                <SignOut size={14} className="mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {results && (
          <div data-testid="search-results" className="border-t border-border bg-sky/10 px-4 py-3 md:px-8">
            <div className="flex items-center justify-between">
              <p className="label-caps">{results.length} permitted result(s)</p>
              <button className="text-xs text-deepblue underline" onClick={() => setResults(null)} data-testid="search-clear">
                Clear
              </button>
            </div>
            <ul className="mt-2 flex flex-wrap gap-2">
              {results.map((r) => (
                <li key={`${r.type}-${r.id}`} className="rounded-full border border-deepblue/25 bg-card px-3 py-1 text-xs">
                  <span className="label-caps mr-2">{r.type.replace(/_/g, " ")}</span>
                  {r.label}
                </li>
              ))}
            </ul>
          </div>
        )}
      </header>

      <div className="flex">
        <aside
          className={`fixed inset-y-0 left-0 z-20 shrink-0 overflow-y-auto border-r border-border/70 bg-card/70 pb-16 pt-20 backdrop-blur-xl transition-all duration-300 lg:static lg:translate-x-0 lg:pt-6 ${
            open ? "translate-x-0" : "-translate-x-full"
          } ${collapsed ? "w-64 lg:w-0 lg:overflow-hidden lg:border-r-0" : "w-64"}`}
        >
          <nav className="flex flex-col" onClick={() => setOpen(false)}>
            <Section title="Platform" testid="nav-group-platform">
              <NavItem to="/" icon={Gauge} label="Home dashboard" testid="nav-home" />
              <NavItem to="/my-work" icon={Briefcase} label="My Work" testid="nav-my-work" />
              <NavItem to="/notifications" icon={Bell} label="Notifications" testid="nav-notifications" />
              <NavItem to="/documents" icon={Folders} label="Document centre" testid="nav-documents" />
              <NavItem to="/reports" icon={ChartBar} label="Report centre" testid="nav-reports" />
              <NavItem to="/audit" icon={FileText} label="Audit & activity" testid="nav-audit" />
            </Section>

            <Section title="Frameworks" testid="nav-group-frameworks">
              {hasFramework("dinacharya") && (
                <NavItem to="/dinacharya" icon={CalendarCheck} label="Dinacharya" testid="nav-dinacharya" accent="bg-teal" />
              )}
              {hasFramework("panchatanthra") && (
                <NavItem to="/panchatanthra" icon={ClipboardText} label="Panchatanthra" testid="nav-panchatanthra" accent="bg-terracotta" />
              )}
              {hasFramework("viveka_kartha") && (
                <NavItem to="/viveka-kartha" icon={Lock} label="Viveka Kartha" testid="nav-viveka-kartha" accent="bg-plum" />
              )}
              {access && (access?.frameworks || []).length === 0 && (
                <p className="px-4 text-xs text-muted-foreground">No framework activated for you</p>
              )}
            </Section>

            {isPlatformAdmin && !presenting && (
              <Section title="Administration" testid="nav-group-administration">
                <NavItem to="/admin/workbooks" icon={Table} label="Questionnaire workbooks" testid="nav-admin-workbooks" />
                <NavItem to="/admin/tenants" icon={Buildings} label="Tenant register" testid="nav-admin-tenants" />
                <NavItem to="/admin/dinacharya-links" icon={LinkSimple} label="Dinacharya links" testid="nav-admin-dinacharya-links" />
                <NavItem to="/admin/wealth-configuration" icon={Vault} label="Wealth configuration" testid="nav-admin-wealth-config" />
                <NavItem to="/admin/fp-configuration" icon={Table} label="Financial engine configuration" testid="nav-admin-fp-config" />
                <NavItem to="/admin/frameworks" icon={Stack} label="Framework activation" testid="nav-admin-frameworks" />
                <NavItem to="/admin/org" icon={TreeStructure} label="Org hierarchy" testid="nav-admin-org" />
                <NavItem to="/admin/roles" icon={Users} label="Roles & grants" testid="nav-admin-roles" />
                <NavItem to="/admin/confidentiality" icon={ShieldCheck} label="Confidentiality" testid="nav-admin-confidentiality" />
                <NavItem to="/admin/configurations" icon={Sliders} label="Configuration versions" testid="nav-admin-configurations" />
                <NavItem to="/admin/workflows" icon={GitBranch} label="Workflow manager" testid="nav-admin-workflows" />
                <NavItem to="/admin/notifications" icon={EnvelopeSimple} label="Notification manager" testid="nav-admin-notifications" />
                <NavItem to="/admin/imports" icon={ArrowsLeftRight} label="Imports & jobs" testid="nav-admin-imports" />
              </Section>
            )}
          </nav>
        </aside>

        <main className="relative z-10 min-h-[calc(100vh-57px)] w-full px-5 py-8 md:px-12 md:py-10">
          <div className="mb-5 hidden items-center gap-2 lg:flex">
            <button
              data-testid="sidebar-collapse-toggle"
              onClick={() => setCollapsed((v) => !v)}
              title={collapsed ? "Show navigation" : "Hide navigation"}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border bg-card text-navy transition-colors duration-300 hover:border-deepblue hover:bg-navy hover:text-white"
            >
              <SidebarSimple size={14} />
            </button>
            {fit.on && (
              <div className="flex items-center gap-1 rounded-full border border-deepblue/30 bg-card px-2 py-1"
                   data-testid="presenter-zoom-controls">
                <button data-testid="presenter-zoom-out" onClick={() => fit.nudge(-0.05)}
                        title="Zoom out" className="grid h-6 w-6 place-items-center rounded-full text-navy hover:bg-sky/40">
                  <MinusIcon size={12} />
                </button>
                <button data-testid="presenter-zoom-auto" onClick={fit.autoFit}
                        title="Auto-fit this screen"
                        className="px-1.5 font-mono text-[10px] tabular-nums text-navy">
                  {Math.round(fit.zoom * 100)}%
                </button>
                <button data-testid="presenter-zoom-in" onClick={() => fit.nudge(0.05)}
                        title="Zoom in" className="grid h-6 w-6 place-items-center rounded-full text-navy hover:bg-sky/40">
                  <PlusIcon size={12} />
                </button>
              </div>
            )}
          </div>
          <div ref={fit.ref} style={fit.on ? { zoom: fit.zoom } : undefined}
               data-testid="presenter-fit-surface">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};
